// Deterministic variant identity, identical to the backend writer
// (asset_geometry_operations.variantId): 'v-' + SHA-256(JCS({definitionDigest, parameters})).
// Synchronous on purpose: owner commands must store the identity in the same draft.
const HASH = /^[0-9a-f]{64}$/;
const check = (ok, message) => { if (!ok) throw new TypeError(`Asset variant: ${message}`); };

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
]);

/** Plain SHA-256 of UTF-8 text; small inputs only (identity documents). */
export function sha256HexSync(text) {
  const input = new TextEncoder().encode(text);
  check(input.length <= 64 * 1024, 'identity input too large');
  const length = Math.ceil((input.length + 9) / 64) * 64;
  const bytes = new Uint8Array(length);
  bytes.set(input); bytes[input.length] = 0x80;
  const view = new DataView(bytes.buffer), bits = input.length * 8;
  view.setUint32(length - 8, Math.floor(bits / 0x100000000)); view.setUint32(length - 4, bits >>> 0);
  const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
  const w = new Uint32Array(64);
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));
  for (let offset = 0; offset < length; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, k] = h;
    for (let i = 0; i < 64; i++) {
      const t1 = (k + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i] + w[i]) >>> 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      k = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    [a, b, c, d, e, f, g, k].forEach((value, i) => { h[i] = (h[i] + value) >>> 0; });
  }
  return [...h].map(value => value.toString(16).padStart(8, '0')).join('');
}

/**
 * Bounded variant parameters: a flat object of finite non-negative numbers with
 * plain keys (0 is a valid assembly offset; profiles/panels are bounded by their
 * capability limits). Returned in canonical (sorted) key order and frozen.
 */
export function createAssetVariantParameters(value) {
  check(value && typeof value === 'object' && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype, 'parameters object required');
  const keys = Reflect.ownKeys(value);
  check(keys.length > 0 && keys.length <= 16, 'bounded parameters required');
  const copy = {};
  for (const key of keys) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    check(typeof key === 'string' && /^[a-z][A-Za-z0-9]{0,63}$/.test(key) && descriptor.enumerable && Object.hasOwn(descriptor, 'value')
      && typeof descriptor.value === 'number' && Number.isFinite(descriptor.value) && descriptor.value >= 0 && descriptor.value <= 1e6, 'invalid parameter');
    copy[key] = descriptor.value;
  }
  return Object.freeze(Object.fromEntries(Object.keys(copy).sort().map(key => [key, copy[key]])));
}

export function assetVariantId(definitionDigest, parameters) {
  check(typeof definitionDigest === 'string' && HASH.test(definitionDigest), 'invalid definition digest');
  const checked = createAssetVariantParameters(parameters);
  // JCS for this fixed shape: sorted keys, ECMAScript number serialisation.
  const params = Object.keys(checked).map(key => `${JSON.stringify(key)}:${JSON.stringify(checked[key])}`).join(',');
  return `v-${sha256HexSync(`{"definitionDigest":${JSON.stringify(definitionDigest)},"parameters":{${params}}}`)}`;
}
